import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Theme } from "klayowebshared";
import { ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { Allowed_Mime_Types, CHECKLIST_TYPE, FILE_TYPE, Max_File_Size } from "../constants";
import { useFileUploadDialog } from "../hooks/useFileUploadDialog";
import { IChecklistOption } from "../interfaces/CheckLists";
import { ParticipantChecklist, ParticipantChecklists } from "../interfaces/Participants";
import {
  PracticalUploadedFile,
  SELECTED_COMPETENCY_TYPE,
  SELECTED_FILE_TYPE
} from "../interfaces/Practical";
import { useLocale } from "../providers/LocaleProvider";
import { Button } from "./Button";
import { Dialog } from "./dialogs/Dialog";
import { DocumentList } from "./table/DocumentList";

export const itemSx = {
  textTransform: "none",
  px: 2.5,
  py: 1,
  borderRadius: "20px !important",
  fontFamily: "Inter !important",
  border: "1px solid var(--neon-blue-nb-100) !important",
  "&.Mui-selected": {
    color: "white",
    bgcolor: "primary.dark"
  },
  "&.Mui-selected:hover": {
    color: "white",
    bgcolor: "primary.dark"
  }
};

type Props = {
  open: boolean;
  onClose: () => void;
  theme: typeof Theme;
  onUploadAttachments: (
    attachments: PracticalUploadedFile[],
    selectCompetency?: SELECTED_COMPETENCY_TYPE
  ) => void;
  onUploadChecklist?: (
    checklist: ParticipantChecklist[],
    selectCompetency: SELECTED_COMPETENCY_TYPE
  ) => void;
  checklistsUploaded?: ParticipantChecklists[];
  uploadFilesLoading?: boolean;
};
export const FileUploadDialog = (props: Props) => {
  const {
    open,
    onClose,
    theme,
    onUploadAttachments,
    onUploadChecklist,
    checklistsUploaded,
    uploadFilesLoading
  } = props;

  const { translate } = useLocale();

  const {
    selectCompetency,
    files,
    selectedFileType,
    setFiles,
    handleSelectCompetencyChange,
    onCloseDialog,
    setSelectedChecklist,
    selectedChecklist,
    onDeleteChecklist,
    setSelectedFileType
  } = useFileUploadDialog({
    onClose
  });

  const onAddAttachments = () => {
    onUploadAttachments(files, selectCompetency);
    onUploadChecklist?.(selectedChecklist, selectCompetency);
    setSelectedChecklist([]);
    setFiles([]);
    setSelectedFileType(SELECTED_FILE_TYPE.DOCUMENT);
  };

  return (
    <Dialog open={open} onClose={onCloseDialog as any} theme={theme}>
      <DialogTitle sx={{ fontSize: "24px", marginBottom: "20px" }}>
        {translate("fileUpload.title")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "310px" }}>
        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            sx={{
              fontSize: "14px !important",
              fontFamily: "Inter !important",
              fontWeight: "500 !important",
              marginLeft: "15px"
            }}
          >
            {translate("fileUpload.attachToCompetency")}
          </Typography>
          <ToggleButtonGroup
            value={selectCompetency}
            exclusive
            onChange={handleSelectCompetencyChange as any}
            sx={{ gap: 2, marginTop: "10px" }}
          >
            <ToggleButton value={SELECTED_COMPETENCY_TYPE.SELECTED} sx={itemSx}>
              {translate("common.selected")}
            </ToggleButton>
            <ToggleButton value={SELECTED_COMPETENCY_TYPE.ALL} sx={itemSx}>
              {translate("common.all")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <DocumentList
          theme={theme}
          label=''
          files={files?.map((file) => file.file)}
          onFilesChange={(e: ChangeEvent<HTMLInputElement>, files) => {
            const practicalFile = Object.values(files).map((file) => {
              return {
                fileId: uuidv4(),
                file: file,
                fileType: FILE_TYPE.PARTICIPANT
              };
            });
            setFiles(practicalFile);
          }}
          allowedMimeTypes={Allowed_Mime_Types}
          maxFileSize={Max_File_Size}
          errorMimeType={translate("messages.validEvidenceFile")}
          errorFileSize={translate("messages.validEvidenceFileSize")}
          isShowFileType={true}
          isShowInlineFiles={true}
          onChange={
            ((e: any, value: IChecklistOption[]) => {
              setSelectedChecklist((value as any) || []);
            }) as any
          }
          onDeleteChecklist={onDeleteChecklist}
          selectedChecklist={selectedChecklist as any}
          setSelectedFileType={setSelectedFileType}
          checklistsUploaded={checklistsUploaded as any}
          checklistType={CHECKLIST_TYPE.PARTICIPANT}
          onUpdateChecklistData={(updatedChecklist: any) => {
            setSelectedChecklist(updatedChecklist);
          }}
          translate={translate}
        />
      </DialogContent>
      <DialogActions sx={{ padding: "40px", gap: "16px" }}>
        <Button label={translate("common.cancel")} onClick={onCloseDialog} />
        <Button
          label={translate("common.upload")}
          onClick={onAddAttachments}
          disabled={(files.length === 0 && selectedChecklist.length === 0) || uploadFilesLoading}
          isLoading={uploadFilesLoading}
          variant='filled'
        />
      </DialogActions>
    </Dialog>
  );
};
